import { getFlightTimeDisplayInfo } from "./flightTimingUtils";
import {
    applyReferenceDateShift,
    buildScheduleTrackKey,
    getDraftFlights,
    getReferenceDisplayTracks,
    getTrackFlights,
    isExecutionReferenceSource,
    isScheduleTrackKey,
    parseTrackKey,
    toArray,
} from "./trackUtils";
import { getTimeWindowFromFlights } from "./canvas/scheduleDraftCanvasHelpers";
import { AIRCRAFT_AVAILABILITY_CONTEXT_PERIOD_MARGIN_DAYS, buildDraftPeriodTimeWindow } from "./aircraftAvailabilityDisplayPolicy";

function normalizeAircraftRegistration(value) {
    return String(value || "").trim().toUpperCase();
}

function getAircraftRegistrationValue(value) {
    if (value && typeof value === "object") {
        return value.registration || value.aircraft_reg || value.aircraftReg || value.reg || "";
    }
    return value;
}

function isDeletedFlight(flight) {
    return flight?.is_deleted || String(flight?.change_status || "").toUpperCase() === "DELETED";
}

function projectReferenceFlightTimes(flights, referenceSource) {
    const shiftedFlights = applyReferenceDateShift(toArray(flights).filter((flight) => !isDeletedFlight(flight)), referenceSource);
    if (!isExecutionReferenceSource(referenceSource)) {
        return shiftedFlights;
    }

    return shiftedFlights.map((flight) => {
        const timeInfo = getFlightTimeDisplayInfo(flight, { useEffectiveTime: true });
        return {
            ...flight,
            std: timeInfo.departureTime || flight.std,
            sta: timeInfo.arrivalTime || flight.sta,
        };
    });
}

function getVisibleReferenceTrackKeys(visibleReferenceTrackIds = [], referenceTrackKeys = []) {
    const orderedKeys = toArray(referenceTrackKeys);
    return orderedKeys.length ? orderedKeys : toArray(visibleReferenceTrackIds).map(buildScheduleTrackKey);
}

function getVisibleReferenceFlights(draft, visibleReferenceTrackIds = [], externalReferenceTracks = [], referenceTrackKeys = []) {
    const tracksById = new Map(getReferenceDisplayTracks(draft).map((track) => [String(track.id), track]));
    const externalByKey = new Map(toArray(externalReferenceTracks).map((referenceSource) => [referenceSource.trackKey, referenceSource]));

    return getVisibleReferenceTrackKeys(visibleReferenceTrackIds, referenceTrackKeys).flatMap((trackKey) => {
        if (isScheduleTrackKey(trackKey)) {
            const track = tracksById.get(parseTrackKey(trackKey).id);
            return track ? projectReferenceFlightTimes(getTrackFlights(track), track) : [];
        }

        const referenceSource = externalByKey.get(trackKey);
        return referenceSource ? projectReferenceFlightTimes(referenceSource.flights, referenceSource) : [];
    });
}

function hasValidFlightTimes(flights) {
    return toArray(flights).some((flight) => [flight?.std, flight?.sta].some((value) => Number.isFinite(new Date(value).getTime())));
}

function unionTimeWindows(windows) {
    const ranges = toArray(windows)
        .map((window) => ({
            start: window?.start ? new Date(window.start).getTime() : NaN,
            end: window?.end ? new Date(window.end).getTime() : NaN,
        }))
        .filter((window) => Number.isFinite(window.start) && Number.isFinite(window.end) && window.end > window.start);
    if (!ranges.length) {
        return null;
    }

    return {
        start: new Date(Math.min(...ranges.map((window) => window.start))).toISOString(),
        end: new Date(Math.max(...ranges.map((window) => window.end))).toISOString(),
    };
}

function getAircraftAvailabilityTimeWindow(flights, draft) {
    const periodWindow = buildDraftPeriodTimeWindow(draft, { marginDays: AIRCRAFT_AVAILABILITY_CONTEXT_PERIOD_MARGIN_DAYS });
    const flightWindow = hasValidFlightTimes(flights) ? getTimeWindowFromFlights(flights, null) : null;
    return unionTimeWindows([periodWindow, flightWindow]) || getTimeWindowFromFlights(flights, draft);
}

export function getAircraftAvailabilitySourceFlights(draft, options = {}) {
    if (!draft) {
        return [];
    }
    return [
        ...getDraftFlights(draft),
        ...getVisibleReferenceFlights(
            draft,
            options.visibleReferenceTrackIds,
            options.externalReferenceTracks,
            options.referenceTrackKeys,
        ),
    ];
}

export function buildAircraftAvailabilityQueryInput(draft, options = {}) {
    if (!draft) {
        return { registrations: [], start: null, end: null, status: "ACTIVE" };
    }

    const flights = getAircraftAvailabilitySourceFlights(draft, options);
    const registrations = [
        ...toArray(draft.aircraft_scope).map(getAircraftRegistrationValue),
        ...flights.map((flight) => flight?.aircraft_reg ?? flight?.aircraftReg),
    ];
    const timeWindow = getAircraftAvailabilityTimeWindow(flights, draft);

    return {
        registrations: [...new Set(registrations.map(normalizeAircraftRegistration).filter(Boolean))].sort(),
        start: timeWindow?.start || null,
        end: timeWindow?.end || null,
        status: "ACTIVE",
    };
}
